import React, { useState, useRef, useEffect } from 'react';
import { Paperclip, Mic, Send, Zap, ChevronLeft, ChevronRight, X, CalendarCheck } from 'lucide-react';
import V4CeoBriefing from './V4CeoBriefing';
import V4MarketingBriefing from './V4MarketingBriefing';
import V4SalesBriefing from './V4SalesBriefing';
import V4SupportBriefing from './V4SupportBriefing';
import V4PeopleBriefing from './V4PeopleBriefing';
import V4FinanceBriefing from './V4FinanceBriefing';
import { useRegisterChatInput } from '../../hooks/useChatInputRegistry';

const AGENTS = [
  { id: 'luc', name: 'Luc', role: 'Personal Growth Partner', thumb: '/Thumbnails/Luc.png', accent: '#00BCD4', gradientFrom: 'from-[#00BCD4]', gradientTo: 'to-[#00897B]' },
  { id: 'marsi', name: 'Marsi', role: 'Marketing & Growth Expert', thumb: '/Thumbnails/Marsi.png', accent: '#FF9500', gradientFrom: 'from-[#FF9500]', gradientTo: 'to-[#E67E00]' },
  { id: 'sally', name: 'Sally', role: 'Sales Expert', thumb: '/Thumbnails/Sally.png', accent: '#FF5C5C', gradientFrom: 'from-[#FF5C5C]', gradientTo: 'to-[#D43D3D]' },
  { id: 'buzz', name: 'Buzz', role: 'Social Media Expert', thumb: '/Thumbnails/Buzz.png', accent: '#47005F', gradientFrom: 'from-[#47005F]', gradientTo: 'to-[#7B1FA2]' },
  { id: 'wright', name: 'Wright', role: 'Copywriting Expert', thumb: '/Thumbnails/Wright.png', accent: '#704e33', gradientFrom: 'from-[#704e33]', gradientTo: 'to-[#8D6E4C]' },
  { id: 'zira', name: 'Zira', role: 'Support Expert', thumb: '/Thumbnails/Ziira.png', accent: '#E91E63', gradientFrom: 'from-[#E91E63]', gradientTo: 'to-[#C2185B]' },
  { id: 'finn', name: 'Finn', role: 'Finance Expert', thumb: '/Thumbnails/Finn.png', accent: '#228B22', gradientFrom: 'from-[#228B22]', gradientTo: 'to-[#1B5E20]' },
  { id: 'dora', name: 'Dora', role: 'Research & Data Analyst', thumb: '/Thumbnails/Dora.png', accent: '#367588', gradientFrom: 'from-[#367588]', gradientTo: 'to-[#265A68]' },
  { id: 'stark', name: 'Stark', role: 'Business Strategist', thumb: '/Thumbnails/Stark.png', accent: '#0047AB', gradientFrom: 'from-[#0047AB]', gradientTo: 'to-[#003080]' },
  { id: 'tali', name: 'Tali', role: "HR & People's Partner", thumb: '/Thumbnails/Tali.png', accent: '#FF9BAA', gradientFrom: 'from-[#FF9BAA]', gradientTo: 'to-[#E57F90]' },
];

const AGENTS_BY_ROLE = {
  ceo: ['luc', 'marsi', 'sally', 'buzz', 'wright', 'zira', 'finn', 'dora', 'stark', 'tali'],
  marketing: ['luc', 'marsi', 'buzz', 'wright', 'dora'],
  sales: ['luc', 'sally', 'dora', 'wright'],
  support: ['luc', 'zira', 'dora'],
  people: ['luc', 'tali', 'dora', 'wright'],
  finance: ['luc', 'finn', 'dora'],
};

const DEFAULT_AGENT_BY_ROLE = {
  ceo: 'luc',
  marketing: 'luc',
  sales: 'luc',
  support: 'luc',
  people: 'luc',
  finance: 'luc',
};

const GREETING_BY_ROLE = {
  ceo: (name) => ({ title: `Good morning, ${name}`, subtitle: "Here's what needs your attention today" }),
  marketing: (name) => ({ title: `Hey ${name}`, subtitle: "Here's your marketing brief for today" }),
  sales: (name) => ({ title: `Hey ${name}`, subtitle: "Here's your pipeline update" }),
  support: (name) => ({ title: `Hey ${name}`, subtitle: "Here's your support overview" }),
  people: (name) => ({ title: `Hey ${name}`, subtitle: "Here's your people & team update" }),
  finance: (name) => ({ title: `Hey ${name}`, subtitle: "Here's your financial overview" }),
};

const V4ChatPanel = ({ isDark, onWorkflowToggle, workflowPanelOpen, onDailyTasksToggle, dailyTasksPanelOpen, activeUser, simState, simClock }) => {
  const defaultAgentId = DEFAULT_AGENT_BY_ROLE[activeUser?.id] || 'luc';
  const defaultAgent = AGENTS.find((a) => a.id === defaultAgentId) || AGENTS[0];
  const [message, setMessage] = useState('');
  useRegisterChatInput(setMessage);
  const [activeAgent, setActiveAgent] = useState(defaultAgent);
  const [agentBarOpen, setAgentBarOpen] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef(null);
  const agentBarRef = useRef(null);
  const prevUserId = useRef(activeUser?.id);

  useEffect(() => {
    if (activeUser?.id !== prevUserId.current) {
      const agentId = DEFAULT_AGENT_BY_ROLE[activeUser?.id] || 'luc';
      setActiveAgent(AGENTS.find((a) => a.id === agentId) || AGENTS[0]);
      setMessage('');
      setAgentBarOpen(false);
      prevUserId.current = activeUser?.id;
    }
  }, [activeUser]);

  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/40' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const chevronBg = isDark ? 'bg-white/[0.06] hover:bg-white/[0.1]' : 'bg-black/[0.04] hover:bg-black/[0.08]';
  const fadeBg = isDark ? 'from-[#0a1628]' : 'from-[#eee8df]';

  const allowedAgentIds = AGENTS_BY_ROLE[activeUser?.id] || AGENTS_BY_ROLE.ceo;
  const visibleAgents = AGENTS.filter((a) => allowedAgentIds.includes(a.id));

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 5);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 5);
  };

  useEffect(() => {
    if (agentBarOpen) {
      setTimeout(checkScroll, 50);
    }
  }, [agentBarOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setAgentBarOpen(false);
    };
    const handleClickOutside = (e) => {
      if (agentBarRef.current && !agentBarRef.current.contains(e.target)) {
        setAgentBarOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const scrollAgents = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 200, behavior: 'smooth' });
      setTimeout(checkScroll, 300);
    }
  };

  const handleInput = (e) => {
    setMessage(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
  };

  const isCeo = activeUser?.id === 'ceo';
  const [ceoFullscreen, setCeoFullscreen] = useState(false);
  const [marketingFullscreen, setMarketingFullscreen] = useState(false);
  const [salesFullscreen, setSalesFullscreen] = useState(false);
  const isMarketing = activeUser?.id === 'marketing';
  const isSales = activeUser?.id === 'sales';
  const isSupport = activeUser?.id === 'support';
  const isPeople = activeUser?.id === 'people';
  const isFinance = activeUser?.id === 'finance';
  const showBriefing = activeAgent.id === 'luc' && (isCeo || isMarketing || isSales || isSupport || isPeople || isFinance);

  return (
    <div className="flex flex-col h-full" data-testid="v4-chat-panel-inner">
      {/* Chat messages area */}
      {showBriefing ? (
        <div className="flex-1 overflow-hidden">
          {isCeo && <V4CeoBriefing isDark={isDark} simState={simState} simClock={simClock} onFullscreenChange={(fs) => setCeoFullscreen(fs)} ripples={simState?.ripples?.arjun} onRippleComplete={(id) => simState?.completeRippledTask?.('arjun', id)} onRippleAck={(id, act) => simState?.acknowledgeRipple?.('arjun', id, act)} />}
          {isMarketing && <V4MarketingBriefing isDark={isDark} onFullscreenChange={(fs) => setMarketingFullscreen(fs)} priyaState={simState?.priyaState} ripples={simState?.ripples?.priya} onRippleComplete={(id) => simState?.completeRippledTask?.('priya', id)} onRippleAck={(id, act) => simState?.acknowledgeRipple?.('priya', id, act)} />}
          {isSales && <V4SalesBriefing isDark={isDark} onFullscreenChange={(fs) => setSalesFullscreen(fs)} davidState={simState?.davidState} ripples={simState?.ripples?.david} onRippleComplete={(id) => simState?.completeRippledTask?.('david', id)} onRippleAck={(id, act) => simState?.acknowledgeRipple?.('david', id, act)} />}
          {isSupport && <V4SupportBriefing isDark={isDark} />}
          {isPeople && <V4PeopleBriefing isDark={isDark} />}
          {isFinance && <V4FinanceBriefing isDark={isDark} />}
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-[80%] flex flex-col items-center">
            <img src={activeAgent.thumb} alt={activeAgent.name} className="w-12 h-12 rounded-full object-cover mb-4" />
            <p className={`text-lg font-semibold ${textPrimary}`}>
              {activeAgent.id === defaultAgentId
                ? (GREETING_BY_ROLE[activeUser?.id] || GREETING_BY_ROLE.ceo)(activeUser?.name?.split(' ')[0] || 'there').title
                : `Chat with ${activeAgent.name}`}
            </p>
            <p className={`text-sm mt-1 ${textSecondary}`}>
              {activeAgent.id === defaultAgentId
                ? (GREETING_BY_ROLE[activeUser?.id] || GREETING_BY_ROLE.ceo)(activeUser?.name?.split(' ')[0] || 'there').subtitle
                : `${activeAgent.name} — ${activeAgent.role}`}
            </p>
          </div>
        </div>
      )}

      {/* Bottom section — hidden when CEO or Marketing is in fullscreen mode (task/risk/email/campaign flow) */}
      {!(isCeo && ceoFullscreen) && !(isMarketing && marketingFullscreen) && !(isSales && salesFullscreen) && (
      <div className="flex justify-center pb-4 px-4">
        <div className="w-[80%]">
          {/* Agent tags — collapsible */}
          {agentBarOpen && (
            <div className="flex items-center mb-3 relative" data-testid="v4-agent-tags" ref={agentBarRef}>
              {/* Left chevron + fade — only when scrolled */}
              {canScrollLeft && (
                <div className="flex items-center flex-shrink-0 relative z-10">
                  <button
                    onClick={() => scrollAgents(-1)}
                    className={`p-1 rounded-lg flex-shrink-0 transition-colors ${chevronBg}`}
                    data-testid="v4-agent-scroll-left"
                  >
                    <ChevronLeft className={`w-4 h-4 ${textSecondary}`} />
                  </button>
                  <div className={`w-8 h-full bg-gradient-to-r ${fadeBg} to-transparent pointer-events-none`} />
                </div>
              )}

              <div
                ref={scrollRef}
                onScroll={checkScroll}
                className="flex items-center gap-2 overflow-x-hidden flex-1"
              >
                {visibleAgents.map((agent) => (
                  <button
                    key={agent.id}
                    onClick={() => setActiveAgent(agent)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full flex-shrink-0 transition-all ${activeAgent.id === agent.id ? `bg-gradient-to-r ${agent.gradientFrom} ${agent.gradientTo} text-white` : (isDark ? 'bg-white/[0.06] text-white/50 hover:bg-white/[0.1]' : 'bg-black/[0.04] text-slate-500 hover:bg-black/[0.08]')}`}
                    data-testid={`v4-pick-agent-${agent.id}`}
                  >
                    <img src={agent.thumb} alt={agent.name} className="w-6 h-6 rounded-full object-cover" />
                    <span className="text-sm font-medium whitespace-nowrap">{agent.name}</span>
                  </button>
                ))}
              </div>

              {/* Right chevron + fade — only when more to scroll */}
              {canScrollRight && (
                <div className="flex items-center flex-shrink-0 relative z-10">
                  <div className={`w-8 h-full bg-gradient-to-l ${fadeBg} to-transparent pointer-events-none`} />
                  <button
                    onClick={() => scrollAgents(1)}
                    className={`p-1 rounded-lg flex-shrink-0 transition-colors ${chevronBg}`}
                    data-testid="v4-agent-scroll-right"
                  >
                    <ChevronRight className={`w-4 h-4 ${textSecondary}`} />
                  </button>
                </div>
              )}

              {/* Collapse button */}
              <button
                onClick={() => setAgentBarOpen(false)}
                className={`p-1 rounded-lg flex-shrink-0 ml-2 transition-colors ${chevronBg}`}
                data-testid="v4-agent-collapse"
              >
                <X className={`w-4 h-4 ${textSecondary}`} />
              </button>
            </div>
          )}

          {/* Chat input — auto-expanding from single line */}
          <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`} data-tour="chat-input">
            <div className="px-4 pt-3 pb-2">
              <textarea
                value={message}
                onChange={handleInput}
                onFocus={() => setAgentBarOpen(false)}
                placeholder={`Message ${activeAgent.name}...`}
                rows={1}
                className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${textPrimary} placeholder:${textSecondary}`}
                style={{ height: 'auto', maxHeight: '200px', overflow: message.split('\n').length > 8 ? 'auto' : 'hidden' }}
                data-testid="v4-chat-input"
              />
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-between px-3 pb-3">
              <div className="flex items-center gap-1">
                {/* Agent switcher toggle — shows active agent, opens bar */}
                <button
                  onClick={() => setAgentBarOpen(!agentBarOpen)}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${agentBarOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}
                  data-testid="v4-agent-switcher"
                  data-tour="agent-switcher"
                >
                  <img src={activeAgent.thumb} alt={activeAgent.name} className="w-5 h-5 rounded-full object-cover" />
                  <span className={`text-xs font-medium ${textPrimary}`}>{activeAgent.name}</span>
                </button>

                <button
                  onClick={onWorkflowToggle}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}
                  data-testid="v4-workflow-selector"
                >
                  <Zap className={`w-3.5 h-3.5 ${textPrimary}`} />
                  <span className={`text-xs font-medium ${textPrimary}`}>Workflows</span>
                </button>
                <button
                  onClick={onDailyTasksToggle}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${dailyTasksPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}
                  data-testid="v4-daily-tasks-selector"
                >
                  <CalendarCheck className={`w-3.5 h-3.5 ${textPrimary}`} />
                  <span className={`text-xs font-medium ${textPrimary}`}>My Day</span>
                </button>
                <button className={`p-1.5 rounded-lg transition-colors ${hoverBg}`} data-testid="v4-attach-btn">
                  <Paperclip className={`w-3.5 h-3.5 ${textSecondary}`} />
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <button className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${hoverBg}`} data-testid="v4-voice-btn">
                  <Mic className={`w-4 h-4 ${textSecondary}`} />
                </button>
                <button
                  className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`}
                  data-testid="v4-send-btn"
                >
                  <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : textSecondary}`} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default V4ChatPanel;

